/* CUSTOM: smart contact form — logic for sections/contact.liquid.
 *
 * Flow
 *   reason picked  -> show the steps/fields for that reason
 *   order reasons  -> order number required (picked from the signed-in customer's
 *                     orders, or typed and normalised to PREFIX + digits)
 *   guests         -> name, email and phone required
 *   submit         -> validate, build subject + details, send via EmailJS.
 *                     If EmailJS is not configured or the request fails, submit the
 *                     native Shopify contact form instead so nothing is lost.
 *
 * Subject (WillDesk threads by subject, so it must be stable for a customer):
 *   order reasons  "Order #TRL5351 | <suffix>"
 *   everything else "Jane Doe (jane@example.com) | <suffix>"
 * Signed-in customers always use their Shopify account name/email in the subject,
 * so the subject does not drift if they retype their name differently.
 */
(() => {
  if (customElements.get('trl-contact')) return;

  const EMAILJS_ENDPOINT = 'https://api.emailjs.com/api/v1.0/email/send';
  const MIN_FILL_MS = 2500; // faster than this is almost certainly a bot

  const $ = (root, sel) => root.querySelector(sel);
  const $$ = (root, sel) => Array.from(root.querySelectorAll(sel));
  const clean = (s) => String(s == null ? '' : s).replace(/\s+/g, ' ').trim();

  const titleCase = (s) =>
    clean(s)
      .toLowerCase()
      .replace(/(^|[\s\-'’])(\p{L})/gu, (m, sep, ch) => sep + ch.toUpperCase());

  class TrlContact extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.startedAt = Date.now();

      try {
        this.config = JSON.parse($(this, 'script[data-config]').textContent);
      } catch (e) {
        this.config = { emailjs: {}, orders: [], customer: null };
      }
      this.orders = this.config.orders || [];
      this.customer = this.config.customer;
      this.orderPrefix = (this.config.orderPrefix || 'TRL').toUpperCase();

      this.form = $(this, '#TrlContactForm');
      this.nativeForm = $(this, '#TrlContactNative');
      this.form.noValidate = true; // we validate ourselves, with clearer messages
      this.reason = $(this, '[data-reason]');
      this.orderPick = $(this, '[data-order-pick]');
      this.orderTyped = $(this, '[data-order-number]');
      this.errorBox = $(this, '[data-error]');

      // The typed order field and the picker carry no name; the normalised value
      // goes into one hidden field, so the native fallback submits exactly one.
      this.orderTyped.removeAttribute('name');
      this.orderFinal = document.createElement('input');
      this.orderFinal.type = 'hidden';
      this.orderFinal.name = 'contact[Order number]';
      this.form.appendChild(this.orderFinal);

      this.reason.addEventListener('change', () => this.update());
      if (this.orderPick) this.orderPick.addEventListener('change', () => this.onOrderPick());
      this.orderTyped.addEventListener('blur', () => {
        const n = this.normaliseOrder(this.orderTyped.value);
        if (n) this.orderTyped.value = n;
        this.renderOrderAssist();
      });
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
      this.form.addEventListener('input', (e) => e.target.removeAttribute('aria-invalid'));

      if ($(this, '[data-posted]')) {
        this.form.hidden = true;
        $(this, '[data-done]').hidden = false;
        return;
      }

      this.setupPicker();
      this.preselectFromUrl();
      this.update();
      this.autofill();
      this.setupChatLink();
    }

    /* ---------- reason-driven visibility ---------- */

    get selected() {
      const opt = this.reason.selectedOptions[0];
      if (!opt || !opt.value) return null;
      return { value: opt.value, key: opt.dataset.key, isOrder: opt.dataset.order === 'true' };
    }

    update() {
      const r = this.selected;
      const key = r ? r.key : '';

      $$(this, '[data-show-when="reason"]').forEach((el) => (el.hidden = !r));
      $$(this, '[data-show-when="order"]').forEach((el) => (el.hidden = !(r && r.isOrder)));
      $$(this, '[data-show-for]').forEach((el) => {
        el.hidden = !key || !el.dataset.showFor.split(',').includes(key);
      });

      // Anything inside a hidden container is disabled: not validated, not submitted.
      $$(this.form, 'input, select, textarea').forEach((el) => {
        if (el.type === 'hidden' || el.name === 'trl_hp' || el === this.reason) return;
        el.disabled = !!el.closest('[hidden]');
      });

      // Renumber the visible steps 1..n
      let n = 0;
      $$(this, '.trl-contact__step').forEach((step) => {
        if (step.hidden) return;
        n += 1;
        const badge = $(step, '.trl-contact__step-title > span');
        if (badge) badge.textContent = n;
      });

      if (this.pInput && this.chosen.length >= 5) this.pInput.disabled = true;
      if (this.picker && this.picker.hidden) this.closeResults();

      this.syncOrderFields();
      this.renderOrderAssist();
    }

    /* ---------- orders ---------- */

    normaliseOrder(raw) {
      let s = String(raw || '').toUpperCase().replace(/[\s#\-]/g, '');
      if (!s) return '';
      if (/^\d+$/.test(s)) s = this.orderPrefix + s;
      return s;
    }

    isValidOrder(s) {
      return new RegExp('^' + this.orderPrefix + '\\d{3,}$').test(s);
    }

    onOrderPick() {
      this.syncOrderFields();
      this.renderOrderAssist();
    }

    syncOrderFields() {
      if (!this.orderPick) return;
      const other = this.orderPick.value === '__other';
      const typedWrap = $(this, '[data-order-typed]');
      typedWrap.hidden = !other;
      this.orderTyped.disabled = !other || !!this.orderPick.closest('[hidden]');
    }

    currentOrderNumber() {
      const r = this.selected;
      if (!r || !r.isOrder) return '';
      if (this.orderPick && this.orderPick.value && this.orderPick.value !== '__other') {
        return this.normaliseOrder(this.orderPick.value);
      }
      return this.normaliseOrder(this.orderTyped.value);
    }

    // An order number typed anywhere (message or other fields) for non-order
    // reasons, e.g. "shipping question about TRL5351". Requires the prefix, so ZIP
    // codes and quantities are never mistaken for orders.
    mentionedOrderNumber() {
      const re = new RegExp('#?\\s*' + this.orderPrefix + '\\s*-?\\s*(\\d{3,})', 'i');
      const fields = $$(this.form, 'input[type="text"], input:not([type]), textarea').filter((el) => !el.disabled && el.name !== 'trl_hp');
      for (const el of fields) {
        const m = (el.value || '').match(re);
        if (m) return this.orderPrefix + m[1];
      }
      return '';
    }

    // The order this message is about: the required one for order reasons,
    // otherwise one the customer mentioned.
    threadOrderNumber() {
      const r = this.selected;
      if (!r) return '';
      return r.isOrder ? this.currentOrderNumber() : this.mentionedOrderNumber();
    }

    findOrder(num) {
      return this.orders.find((o) => this.normaliseOrder(o.name) === num) || null;
    }

    tracking(order) {
      const seen = new Set();
      const out = [];
      (order.items || []).forEach((it) => {
        (it.tracking || []).forEach((t, i) => {
          if (!t || seen.has(t)) return;
          seen.add(t);
          out.push({ number: t, carrier: it.carrier || '', url: it.trackingUrl || '' });
        });
      });
      return out;
    }

    renderOrderAssist() {
      const box = $(this, '[data-order-assist]');
      const r = this.selected;
      const order = r && r.isOrder ? this.findOrder(this.currentOrderNumber()) : null;
      if (!order) {
        box.hidden = true;
        box.innerHTML = '';
        return;
      }

      const esc = (s) => String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      const tracks = this.tracking(order);
      const status = order.cancelled ? 'Cancelled' : order.fulfillment;
      let html =
        '<p><strong>' + esc(order.name) + '</strong> · placed ' + esc(order.date) + ' · ' + esc(status) +
        ' · ' + esc(order.financial) + '</p><ul>' +
        (order.items || []).map((i) => '<li>' + esc(i.qty) + ' × ' + esc(i.title) + (i.variant ? ' — ' + esc(i.variant) : '') + '</li>').join('') +
        '</ul>';

      if (tracks.length) {
        html += '<p class="trl-contact__assist-track">' +
          tracks
            .map((t) =>
              (t.url
                ? '<a href="' + esc(t.url) + '" target="_blank" rel="noopener">Track ' + esc(t.carrier || 'package') + ' ' + esc(t.number) + ' →</a>'
                : esc(t.carrier) + ' ' + esc(t.number)))
            .join('<br>') +
          '</p>';
        if (r.key === 'order_status' || r.key === 'delivery_issue') {
          html += '<p class="trl-hint">The tracking link usually has the latest status. Still need help? Send us a message below.</p>';
        }
      }
      if (order.url) html += '<p class="trl-hint"><a href="' + esc(order.url) + '" target="_blank">View order details →</a></p>';

      box.innerHTML = html;
      box.hidden = false;
    }

    /* ---------- auto-fill ---------- */

    preselectFromUrl() {
      const p = new URLSearchParams(location.search);
      const reason = p.get('reason');
      if (reason) {
        const opt = $$(this.reason, 'option').find((o) => o.dataset.key === reason);
        if (opt) opt.selected = true;
      }
      const order = p.get('order');
      if (order) {
        const n = this.normaliseOrder(order);
        if (this.orderPick && $$(this.orderPick, 'option').some((o) => this.normaliseOrder(o.value) === n)) {
          this.orderPick.value = $$(this.orderPick, 'option').find((o) => this.normaliseOrder(o.value) === n).value;
        } else {
          if (this.orderPick) this.orderPick.value = '__other';
          this.orderTyped.value = n;
        }
      }
    }

    setIfEmpty(sel, value) {
      const el = $(this, sel);
      if (el && !el.value && value) el.value = value;
    }

    async autofill() {
      const c = this.customer;
      if (c && c.address) this.setIfEmpty('[data-autofill="zip"]', c.address.zip);

      // Product: ?product=<handle> or the product page the visitor came from.
      let handle = new URLSearchParams(location.search).get('product');
      let variantId = new URLSearchParams(location.search).get('variant');
      if (!handle && document.referrer) {
        try {
          const ref = new URL(document.referrer);
          if (ref.origin === location.origin) {
            const m = ref.pathname.match(/\/products\/([^/?#]+)/);
            if (m) {
              handle = decodeURIComponent(m[1]);
              variantId = variantId || ref.searchParams.get('variant');
            }
          }
        } catch (e) { /* ignore */ }
      }
      if (handle) this.addProduct(handle, variantId);

      // Cart snapshot (useful context for pre-purchase questions)
      this.cart = null;
      try {
        const res = await fetch('/cart.js');
        if (res.ok) this.cart = await res.json();
      } catch (e) { /* ignore */ }
    }

    /* ---------- product picker (Shopify predictive search, same as the header search) ---------- */

    setupPicker() {
      this.picker = $(this, '[data-product-picker]');
      this.chosen = [];
      if (!this.picker) return;
      this.pInput = $(this.picker, '[data-picker-input]');
      this.pResults = $(this.picker, '[data-picker-results]');
      this.pChosen = $(this.picker, '[data-picker-chosen]');
      this.pValue = $(this.picker, '[data-picker-value]');
      this.pActive = -1;
      this.pItems = [];
      let timer;

      this.pInput.addEventListener('input', () => {
        clearTimeout(timer);
        const q = this.pInput.value.trim();
        if (q.length < 2) return this.closeResults();
        timer = setTimeout(() => this.searchProducts(q), 220);
      });
      this.pInput.addEventListener('keydown', (e) => {
        if (this.pResults.hidden) return;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          const n = this.pItems.length;
          if (!n) return;
          this.pActive = (this.pActive + (e.key === 'ArrowDown' ? 1 : -1) + n) % n;
          this.highlight();
        } else if (e.key === 'Enter') {
          e.preventDefault(); // never submit the form from the search box
          const item = this.pItems[this.pActive >= 0 ? this.pActive : 0];
          if (item) this.pickResult(item);
        } else if (e.key === 'Escape') {
          this.closeResults();
        }
      });
      this.pInput.addEventListener('focus', () => {
        if (this.pItems.length && this.pInput.value.trim().length >= 2) this.openResults();
      });
      document.addEventListener('click', (e) => {
        if (!this.picker.contains(e.target)) this.closeResults();
      });
      this.pChosen.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-remove]');
        if (!btn) return;
        this.chosen.splice(Number(btn.dataset.remove), 1);
        this.renderChosen();
        this.pInput.focus();
      });
      this.pChosen.addEventListener('change', (e) => {
        const sel = e.target.closest('[data-variant]');
        if (!sel) return;
        const p = this.chosen[Number(sel.dataset.variant)];
        p.variantId = sel.value;
        this.serializeProducts();
      });
    }

    async searchProducts(q) {
      const seq = (this.pSeq = (this.pSeq || 0) + 1);
      const base = this.config.searchUrl || '/search/suggest';
      const url = base + '.json?q=' + encodeURIComponent(q) +
        '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last';
      let products = [];
      try {
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          products = (data.resources && data.resources.results && data.resources.results.products) || [];
        }
      } catch (e) { /* ignore */ }
      if (seq !== this.pSeq) return; // a newer search is in flight
      this.pItems = products;
      this.pActive = -1;
      this.renderResults(q);
    }

    renderResults(q) {
      const esc = (s) => String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      if (!this.pItems.length) {
        this.pResults.innerHTML = '<li class="trl-picker__empty">No rugs match “' + esc(q) + '”. Try a colour, style or design name — or just describe it in your message.</li>';
        this.openResults();
        return;
      }
      this.pResults.innerHTML = this.pItems.map((p, i) => {
        const img = (p.featured_image && p.featured_image.url) || p.image || '';
        const thumb = img ? img + (img.includes('?') ? '&' : '?') + 'width=120' : '';
        const added = this.chosen.some((c) => c.handle === p.handle);
        return '<li id="TrlOpt' + i + '" role="option" class="trl-picker__opt" data-i="' + i + '" aria-selected="false">' +
          (thumb ? '<img src="' + esc(thumb) + '" alt="" width="48" height="48" loading="lazy">' : '<span class="trl-picker__noimg"></span>') +
          '<span class="trl-picker__opt-text"><span class="trl-picker__opt-title">' + esc(p.title) + '</span>' +
          '<span class="trl-picker__opt-meta">' + (p.price ? 'From ' + esc(this.priceText(p.price)) : '') + (p.available === false ? ' · Sold out' : '') + (added ? ' · Added' : '') + '</span></span></li>';
      }).join('');
      $$(this.pResults, '[role="option"]').forEach((li) => {
        li.addEventListener('mousedown', (e) => e.preventDefault()); // keep focus in the input
        li.addEventListener('click', () => this.pickResult(this.pItems[Number(li.dataset.i)]));
      });
      this.openResults();
    }

    priceText(price) {
      const n = Number(price);
      if (isNaN(n)) return String(price);
      const cur = this.config.context.currency || 'USD';
      try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(n); } catch (e) { return n.toFixed(2); }
    }

    highlight() {
      $$(this.pResults, '[role="option"]').forEach((li, i) => {
        const on = i === this.pActive;
        li.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) li.scrollIntoView({ block: 'nearest' });
      });
      this.pInput.setAttribute('aria-activedescendant', this.pActive >= 0 ? 'TrlOpt' + this.pActive : '');
    }

    openResults() {
      this.pResults.hidden = false;
      this.pInput.setAttribute('aria-expanded', 'true');
    }

    closeResults() {
      if (!this.pResults) return;
      this.pResults.hidden = true;
      this.pInput.setAttribute('aria-expanded', 'false');
      this.pInput.removeAttribute('aria-activedescendant');
    }

    pickResult(p) {
      this.closeResults();
      this.pInput.value = '';
      this.addProduct(p.handle);
      this.pInput.focus();
    }

    async addProduct(handle, variantId) {
      if (!this.picker || !handle) return;
      if (this.chosen.some((c) => c.handle === handle)) return;
      if (this.chosen.length >= 5) return;
      try {
        const res = await fetch('/products/' + encodeURIComponent(handle) + '.js');
        if (!res.ok) return;
        const prod = await res.json();
        if (this.chosen.some((c) => c.handle === prod.handle) || this.chosen.length >= 5) return;
        const variants = prod.variants || [];
        const hasVariants = variants.length > 1 || (variants[0] && variants[0].title !== 'Default Title');
        this.chosen.push({
          id: prod.id,
          handle: prod.handle,
          title: prod.title,
          image: prod.featured_image ? (prod.featured_image.startsWith('//') ? 'https:' + prod.featured_image : prod.featured_image) : '',
          url: location.origin + '/products/' + prod.handle,
          variants: hasVariants ? variants.map((v) => ({ id: String(v.id), title: v.title, price: v.price, available: v.available, sku: v.sku })) : [],
          variantId: variantId && variants.some((v) => String(v.id) === String(variantId)) ? String(variantId) : '',
          price: prod.price,
        });
        this.renderChosen();
      } catch (e) { /* ignore */ }
    }

    renderChosen() {
      const esc = (s) => String(s || '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      this.pChosen.innerHTML = this.chosen.map((p, i) => {
        const thumb = p.image ? p.image + (p.image.includes('?') ? '&' : '?') + 'width=160' : '';
        const sizes = p.variants.length
          ? '<select data-variant="' + i + '" aria-label="Size for ' + esc(p.title) + '"><option value="">Size — not sure yet</option>' +
            p.variants.map((v) => '<option value="' + v.id + '"' + (v.id === p.variantId ? ' selected' : '') + '>' + esc(v.title) + ' — ' + esc(this.money(v.price)) + (v.available ? '' : ' (sold out)') + '</option>').join('') +
            '</select>'
          : '';
        return '<li class="trl-picker__item">' +
          (thumb ? '<img src="' + esc(thumb) + '" alt="" width="64" height="64">' : '<span class="trl-picker__noimg"></span>') +
          '<div class="trl-picker__item-body"><a href="' + esc(p.url) + '" target="_blank" rel="noopener">' + esc(p.title) + '</a>' + sizes + '</div>' +
          '<button type="button" class="trl-picker__remove" data-remove="' + i + '" aria-label="Remove ' + esc(p.title) + '">×</button></li>';
      }).join('');
      this.pInput.disabled = this.chosen.length >= 5 || !!this.pInput.closest('[hidden]');
      this.pInput.placeholder = this.chosen.length >= 5 ? 'Maximum of 5 rugs added' : this.chosen.length ? 'Add another rug…' : 'Search by name, colour, style or size…';
      this.serializeProducts();
    }

    productLines() {
      return this.chosen.map((p) => {
        const v = p.variants.find((x) => x.id === p.variantId);
        return p.title +
          (v ? ' — ' + v.title + ' · ' + this.money(v.price) : p.variants.length ? '' : ' — one of a kind · ' + this.money(p.price)) +
          ' · ' + p.url + (v ? '?variant=' + v.id : '') +
          (p.image ? ' · Photo: ' + this.sizedImage(p.image, 800) : '');
      });
    }

    serializeProducts() {
      if (this.pValue) this.pValue.value = this.productLines().join('\n');
    }

    setupChatLink() {
      const wrap = $(this, '[data-chat-link]');
      if (!wrap) return;
      let tries = 0;
      const check = () => {
        if (typeof window.launchWillDeskChat === 'function') {
          wrap.hidden = false;
          $(wrap, '[data-open-chat]').addEventListener('click', () => window.launchWillDeskChat());
        } else if (tries++ < 20) {
          setTimeout(check, 500);
        }
      };
      check();
    }

    /* ---------- validation ---------- */

    validate() {
      const errors = [];
      const fail = (el, msg) => {
        if (el) el.setAttribute('aria-invalid', 'true');
        errors.push({ el, msg });
      };
      const r = this.selected;
      if (!r) {
        fail(this.reason, 'Please choose a reason for contacting us.');
        return errors;
      }

      if (r.isOrder) {
        if (this.orderPick && !this.orderPick.value) {
          fail(this.orderPick, 'Please choose the order this is about.');
        } else {
          const n = this.currentOrderNumber();
          const field = this.orderPick && this.orderPick.value !== '__other' ? this.orderPick : this.orderTyped;
          if (!n) fail(field, 'Please enter your order number — it starts with ' + this.orderPrefix + '.');
          else if (!this.isValidOrder(n)) fail(field, 'That order number doesn\'t look right. It should look like ' + this.orderPrefix + '5351.');
        }
      }

      if (r.key === 'address_change') {
        const a = $(this, '#TrlNewAddress');
        if (!clean(a.value)) fail(a, 'Please enter the new shipping address.');
      }

      $$(this, '[data-required-for]').forEach((el) => {
        if (el.disabled) return;
        if (el.dataset.requiredFor.split(',').includes(r.key) && !clean(el.value)) {
          const label = $(this, 'label[for="' + el.id + '"]');
          fail(el, 'Please fill in ' + (label ? clean(label.textContent.replace('*', '')) : 'this field').toLowerCase() + '.');
        }
      });

      const name = $(this, '#TrlName');
      const email = $(this, '#TrlEmail');
      const phone = $(this, '#TrlPhone');
      if (!clean(name.value)) fail(name, 'Please enter your name.');
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(clean(email.value))) fail(email, 'Please enter a valid email address.');
      const digits = phone.value.replace(/\D/g, '');
      if (!this.customer && digits.length < 7) fail(phone, 'Please enter a phone number we can reach you on.');
      else if (phone.value && digits.length > 0 && digits.length < 7) fail(phone, 'That phone number looks too short.');

      const msg = $(this, '[data-message]');
      if (clean(msg.value).length < 3) fail(msg, 'Please tell us how we can help.');

      return errors;
    }

    showErrors(errors) {
      if (!errors.length) {
        this.errorBox.hidden = true;
        this.errorBox.innerHTML = '';
        return;
      }
      this.errorBox.innerHTML = '<ul>' + errors.map((e) => '<li>' + e.msg + '</li>').join('') + '</ul>';
      this.errorBox.hidden = false;
      const first = errors.find((e) => e.el);
      if (first) first.el.focus({ preventScroll: true });
      this.errorBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    /* ---------- subject + details ---------- */

    identity() {
      // Signed-in: the account's name/email, so the subject never drifts.
      const c = this.customer;
      const name = c && c.name ? clean(c.name) : titleCase($(this, '#TrlName').value);
      const email = (c && c.email ? c.email : clean($(this, '#TrlEmail').value)).toLowerCase();
      return { name, email };
    }

    buildSubject() {
      const suffix = clean(this.config.subjectSuffix) || 'Support';
      const r = this.selected;
      const num = this.threadOrderNumber();
      if (num) return 'Order #' + num + ' | ' + suffix;
      const id = this.identity();
      return id.name + ' (' + id.email + ') | ' + suffix;
    }

    money(cents) {
      const cur = (this.cart && this.cart.currency) || this.config.context.currency || 'USD';
      try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(cents / 100);
      } catch (e) {
        return (cents / 100).toFixed(2) + ' ' + cur;
      }
    }

    /* What goes in the email. Customer-facing only: this text is quoted back to
       the customer whenever the thread is replied to, so no admin links, device,
       cart, customer stats or other internal data. */
    customerFields() {
      return $$(this.form, '[name^="contact["]').filter((el) => {
        if (el.disabled || el.type === 'hidden') return false;
        return !/^contact\[(Reason|name|email|Phone|body)\]$/.test(el.name) && clean(el.value);
      }).map((el) => [el.name.slice(8, -1), clean(el.value)]);
    }

    orderSummary() {
      const num = this.threadOrderNumber();
      if (!num) return null;
      return { num, order: this.findOrder(num) };
    }

    buildDetails(full = true) {
      const L = [];
      const r = this.selected;
      if (full) L.push('Reason: ' + r.value);

      const os = this.orderSummary();
      if (os) {
        const o = os.order;
        L.push('Order: ' + os.num + (o ? ' — placed ' + o.date + ' · ' + (o.cancelled ? 'Cancelled' : o.fulfillment) : ''));
        if (o) {
          (o.items || []).forEach((i) => L.push('  • ' + i.qty + ' × ' + i.title + (i.variant ? ' — ' + i.variant : '')));
          this.tracking(o).forEach((t) => L.push('  Tracking: ' + [t.carrier, t.number, t.url].filter(Boolean).join(' · ')));
        }
      }

      if (this.chosen && this.chosen.length && !(this.picker && this.picker.hidden)) {
        L.push('Rugs:');
        this.productLines().forEach((line) => L.push('  • ' + line));
      }

      if (!full) return L.join('\n');
      this.customerFields().forEach(([k, v]) => L.push(k + ': ' + v));

      L.push('Contact: ' + [clean($(this, '#TrlName').value), clean($(this, '#TrlEmail').value), clean($(this, '#TrlPhone').value)].filter(Boolean).join(' · '));
      return L.join('\n');
    }

    sizedImage(url, width) {
      if (!url) return '';
      if (url.startsWith('//')) url = 'https:' + url;
      return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
    }

    /* HTML version for the EmailJS template ({{{details_html}}}): the same facts as
       buildDetails(), with rug photos. */
    buildDetailsHtml() {
      const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
      const link = (url, text) => '<a href="' + esc(url) + '" style="color:#111">' + esc(text) + '</a>';
      const row = (k, v) => '<tr><td style="padding:3px 14px 3px 0;color:#666;vertical-align:top;white-space:nowrap">' + esc(k) + '</td><td style="padding:3px 0;vertical-align:top">' + v + '</td></tr>';
      const card = (img, html) =>
        '<table role="presentation" cellpadding="0" cellspacing="0" style="margin:6px 0;border-collapse:collapse"><tr>' +
        (img ? '<td style="padding-right:12px;vertical-align:top"><img src="' + esc(img) + '" width="80" style="width:80px;height:auto;border-radius:6px;display:block" alt=""></td>' : '') +
        '<td style="vertical-align:top;font-size:14px;line-height:1.5">' + html + '</td></tr></table>';
      const r = this.selected;
      let rows = row('Reason', esc(r.value));

      const os = this.orderSummary();
      if (os) {
        const o = os.order;
        let v = esc(os.num) + (o ? ' — placed ' + esc(o.date) + ' · ' + esc(o.cancelled ? 'Cancelled' : o.fulfillment) : '');
        if (o) {
          (o.items || []).forEach((i) => { v += card(i.image, esc(i.qty) + ' × ' + esc(i.title) + (i.variant ? '<br>' + esc(i.variant) : '')); });
          this.tracking(o).forEach((t) => { v += '<div>Tracking: ' + (t.url ? link(t.url, (t.carrier ? t.carrier + ' ' : '') + t.number) : esc(t.carrier + ' ' + t.number)) + '</div>'; });
        }
        rows += row('Order', v);
      }

      if (this.chosen && this.chosen.length && !(this.picker && this.picker.hidden)) {
        rows += row('Rugs', this.chosen.map((p) => {
          const v = p.variants.find((x) => x.id === p.variantId);
          return card(this.sizedImage(p.image, 240),
            link(p.url + (v ? '?variant=' + v.id : ''), p.title) +
            (v ? '<br>' + esc(v.title) + ' · ' + esc(this.money(v.price)) : p.variants.length ? '' : '<br>One of a kind · ' + esc(this.money(p.price))));
        }).join(''));
      }

      this.customerFields().forEach(([k, v]) => { rows += row(k, esc(v)); });
      rows += row('Contact', esc([clean($(this, '#TrlName').value), clean($(this, '#TrlEmail').value), clean($(this, '#TrlPhone').value)].filter(Boolean).join(' · ')));

      return '<table role="presentation" cellpadding="0" cellspacing="0" style="border-collapse:collapse;font-family:-apple-system,Segoe UI,Helvetica,Arial,sans-serif;font-size:14px;line-height:1.5;color:#111">' + rows + '</table>';
    }

    /* ---------- submit ---------- */

    async onSubmit(e) {
      e.preventDefault();
      if (this.sending) return;

      // Bot traps: honeypot filled, or form completed impossibly fast.
      if ($(this.form, '[name="trl_hp"]').value || Date.now() - this.startedAt < MIN_FILL_MS) {
        this.showDone();
        return;
      }

      const errors = this.validate();
      this.showErrors(errors);
      if (errors.length) return;

      const subject = this.buildSubject();
      const details = this.buildDetails();
      this.orderFinal.value = this.currentOrderNumber();
      this.orderFinal.disabled = !this.orderFinal.value;
      if (this.pValue) this.pValue.value = this.picker.hidden ? '' : this.productLines().join('\n');
      $(this, '[data-details]').value = this.buildDetails(false);
      $(this, '[data-details]').disabled = !$(this, '[data-details]').value;

      const btn = $(this.form, '[type="submit"]');
      this.sending = true;
      if (btn) btn.setAttribute('aria-busy', 'true');

      const ej = this.config.emailjs || {};
      const configured = ej.publicKey && ej.serviceId && ej.templateId;
      if (configured) {
        const id = this.identity();
        const params = {
          subject,
          from_name: clean($(this, '#TrlName').value),
          reply_to: clean($(this, '#TrlEmail').value),
          customer_email: id.email,
          customer_phone: clean($(this, '#TrlPhone').value),
          reason: this.selected.value,
          order_number: this.threadOrderNumber(),
          message: $(this, '[data-message]').value.trim(),
          details,
          details_html: this.buildDetailsHtml(),
        };
        try {
          const res = await fetch(EMAILJS_ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ service_id: ej.serviceId, template_id: ej.templateId, user_id: ej.publicKey, template_params: params }),
          });
          if (res.ok) {
            this.showDone();
            return;
          }
          console.warn('[contact] EmailJS returned', res.status, await res.text());
        } catch (err) {
          console.warn('[contact] EmailJS request failed', err);
        }
      }

      // Fallback: Shopify's native contact email (fixed subject; everything else in the body).
      this.submitNative();
    }

    // Copy the visible form's fields into the hidden Shopify contact form and post it.
    submitNative() {
      const box = $(this.nativeForm, '[data-native-fields]');
      box.innerHTML = '';
      new FormData(this.form).forEach((value, name) => {
        if (!name.startsWith('contact[')) return;
        const input = document.createElement(name === 'contact[body]' ? 'textarea' : 'input');
        if (input.tagName === 'INPUT') input.type = 'hidden';
        input.name = name;
        input.value = value;
        input.hidden = true;
        box.appendChild(input);
      });
      HTMLFormElement.prototype.submit.call(this.nativeForm);
    }

    showDone() {
      const r = this.selected;
      const first = clean($(this, '#TrlName').value).split(' ')[0];
      const about = r && r.isOrder && this.currentOrderNumber() ? ' about order ' + this.currentOrderNumber() : '';
      $(this, '[data-done-text]').textContent =
        (first ? 'Thanks, ' + titleCase(first) + '. ' : '') + 'We\'ve received your message' + about +
        ' and will reply to ' + clean($(this, '#TrlEmail').value) + ' as soon as we can.';
      this.form.hidden = true;
      const done = $(this, '[data-done]');
      done.hidden = false;
      done.focus();
      done.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  customElements.define('trl-contact', TrlContact);
})();
